/**
 * Malware analysis.
 *
 * The screen is honest about what it did. Static analysis runs here and is real; detonation
 * requires an isolated runner, and with none configured the verdict says so and cannot be
 * "clean". "We did not look" reported as "we looked and it was fine" is the single most harmful
 * thing a malware analysis screen can do.
 *
 * # Why the examples come from the API
 *
 * Earlier drafts embedded example samples so an operator could click one and watch the analyzer
 * react. The endpoint agent on the development machine quarantined the source file — the literal
 * obfuscation and recovery-inhibition strings are a recognized signature, and it was right to.
 * The examples now live in the Rust crate beside the analyzer that understands them and arrive
 * over `/api/sandbox/examples`. That is the better arrangement anyway: when a detection marker
 * changes, the example exercising it is in the same place.
 */

import { useState } from 'react'
import { api, type SandboxReport } from '../api'
import {
  Async,
  Card,
  Chip,
  Mono,
  Note,
  ObservableList,
  SeverityChip,
  fmtBytes,
  useAsync,
  useToast,
} from '../components/ui'

interface Example {
  label: string
  filename: string
  content: string
  note: string
}

export default function MalwareAnalysis() {
  const toast = useToast()
  const examples = useAsync<Example[]>(() => api.get<Example[]>('/sandbox/examples'), [])
  const [filename, setFilename] = useState('sample.txt')
  const [content, setContent] = useState('')
  const [base64, setBase64] = useState(false)
  const [report, setReport] = useState<SandboxReport | null>(null)
  const [busy, setBusy] = useState(false)

  function analyze() {
    setBusy(true)
    api
      .post<SandboxReport>('/sandbox/analyze', { filename, content, base64 })
      .then((r) => {
        setReport(r)
        toast('ok', `Analyzed — verdict: ${r.verdict}`)
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Malware analysis</h1>
          <div className="sub">
            Static analysis reads the sample without running it — format, entropy, strings,
            capability indicators and network entities. Detonation needs an isolated runner and
            deliberately does not happen in this process.
          </div>
        </div>
      </div>

      <div className="grid cols-2">
        <Card title="Submit a sample">
          <div className="field">
            <label htmlFor="fn">File name</label>
            <input id="fn" value={filename} onChange={(e) => setFilename(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="content">Content</label>
            <div className="row wrap" style={{ marginBottom: 6 }}>
              <span className="small muted">Try:</span>
              <Async state={examples} empty={null}>
                {(list) => (
                  <>
                    {list.map((ex) => (
                      <button
                        key={ex.label}
                        className="sm ghost"
                        title={ex.note}
                        onClick={() => {
                          setContent(ex.content)
                          setFilename(ex.filename)
                          setBase64(false)
                        }}
                      >
                        {ex.label}
                      </button>
                    ))}
                  </>
                )}
              </Async>
              <label className="btn sm" style={{ margin: 0 }}>
                Upload a file
                <input
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    if (f.size > 8 * 1024 * 1024) {
                      toast('error', 'Files over 8 MB are better handled by a real runner than pasted into a browser.')
                      return
                    }
                    setFilename(f.name)
                    f.arrayBuffer()
                      .then((buf) => {
                        const bytes = new Uint8Array(buf)
                        let bin = ''
                        // Chunked: spreading a large array into String.fromCharCode overflows the
                        // call stack on real binaries.
                        for (let i = 0; i < bytes.length; i += 8192) {
                          bin += String.fromCharCode(...bytes.subarray(i, i + 8192))
                        }
                        setContent(btoa(bin))
                        setBase64(true)
                      })
                      .catch(() => toast('error', 'Could not read that file'))
                  }}
                />
              </label>
            </div>
            <textarea
              id="content"
              rows={12}
              placeholder="Paste script content, or upload a file."
              value={content}
              onChange={(e) => {
                setContent(e.target.value)
                setBase64(false)
              }}
            />
            <div className="hint">
              {base64 ? 'Uploaded as base64 — binary content.' : `${content.length} characters.`} The
              sample never leaves this deployment.
            </div>
          </div>

          <button className="primary" onClick={analyze} disabled={busy || !content.trim()}>
            {busy ? 'Analyzing…' : 'Analyze'}
          </button>
        </Card>

        {report ? (
          <div className="stack">
            <Card
              title="Verdict"
              actions={
                <Chip
                  tone={
                    report.verdict === 'malicious'
                      ? 'critical'
                      : report.verdict === 'suspicious'
                        ? 'high'
                        : report.verdict === 'clean'
                          ? 'ok'
                          : 'neutral'
                  }
                >
                  {report.verdict}
                </Chip>
              }
            >
              <p>{report.summary}</p>
              {report.dynamic_unavailable_reason && <Note tone="warn">{report.dynamic_unavailable_reason}</Note>}
              <dl className="kv" style={{ marginTop: 10 }}>
                <dt>SHA-256</dt>
                <dd>
                  <Mono copy>{report.sha256}</Mono>
                </dd>
                <dt>Format</dt>
                <dd>{report.static_analysis.format.replace(/_/g, ' ')}</dd>
                <dt>Size</dt>
                <dd>{fmtBytes(report.static_analysis.size_bytes)}</dd>
                <dt>Entropy</dt>
                <dd>
                  <span className="tnum">{report.static_analysis.entropy.toFixed(2)}</span>{' '}
                  <span className="faint small">
                    bits/byte
                    {report.static_analysis.entropy > 7.2
                      ? ' — compressed, encrypted or packed'
                      : report.static_analysis.entropy < 5
                        ? ' — ordinary text or code'
                        : ''}
                  </span>
                </dd>
              </dl>
            </Card>

            {report.static_analysis.findings.length > 0 && (
              <Card title={`Findings (${report.static_analysis.findings.length})`}>
                <div className="stack" style={{ gap: 10 }}>
                  {report.static_analysis.findings.map((f) => (
                    <div key={f.id}>
                      <div className="row wrap" style={{ gap: 8 }}>
                        <SeverityChip severity={f.severity} />
                        <strong>{f.title}</strong>
                        {f.techniques.map((t) => (
                          <Chip key={t} tone="outline">
                            {t}
                          </Chip>
                        ))}
                      </div>
                      <div className="small muted" style={{ marginTop: 3 }}>
                        {f.detail}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {report.static_analysis.observables.length > 0 && (
              <Card title="Entities found in the sample" subtitle="Pivot points for intelligence and correlation">
                <ObservableList observables={report.static_analysis.observables} max={40} />
              </Card>
            )}

            {report.static_analysis.imports.length > 0 && (
              <Card title="Capability indicators">
                <div className="row wrap">
                  {report.static_analysis.imports.map((i) => (
                    <Chip key={i} tone="outline">
                      {i}
                    </Chip>
                  ))}
                </div>
                <p className="small muted" style={{ marginTop: 8 }}>
                  Legitimate software calls most of these. They indicate where to look, not what the
                  sample does.
                </p>
              </Card>
            )}

            <Card title={`Strings (${report.static_analysis.strings.length})`}>
              <pre
                className="mono"
                style={{ maxHeight: 260, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
              >
                {report.static_analysis.strings.join('\n')}
              </pre>
            </Card>
          </div>
        ) : (
          <Card title="How this works">
            <p className="small muted">
              Static analysis is safe to run on the control plane because nothing in it interprets
              the sample as instructions: it reads bytes, measures entropy, identifies the format,
              extracts printable strings, and pulls network entities out of them.
            </p>
            <p className="small muted">
              Detonation is a different matter. It needs a disposable, network-isolated virtual
              machine with a snapshot to revert to, and running it near the control plane would put
              the SIEM inside the blast radius of the malware it is analyzing. Until a runner is
              configured, a static-only pass can conclude <em>suspicious</em> but never{' '}
              <em>clean</em> — because a sample that did nothing observable while nobody ran it is
              not evidence of anything.
            </p>
            <Note>
              The built-in examples carry the <em>markers</em> a real sample would, not working
              payloads, and are served from the API rather than embedded in this page — a source
              file full of literal obfuscation strings is itself a signature, as this page twice
              discovered.
            </Note>
          </Card>
        )}
      </div>
    </>
  )
}
